/** external */
import _ from "lodash";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

/** internal */
import ACLStore from "lib/aclStore";
import {
  DeleteOutlined,
  UploadOutlined,
  OneDriveColored,
  GoogleDriveColored,
} from "@toddle-edu/ds-icons";
import { MediaCard, IconButton, Tooltip } from "@toddle-edu/ds-web";
import GooglePicker from "UIComponents/GooglePicker";
import DropzoneComponent from "UIComponents/DropzoneComponent";
import MicrosoftPicker from "UIComponents/MicrosoftPicker";
import { getMaxSize } from "Utils";
import { getCurriculumProgramFreeStatus } from "Platform/modules/PlatformModule";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import client from "lib/apolloClient/client";
import { compose } from "lib/apolloClient";
import { ALLOWED_FILE_TYPES_V2 } from "AppComponents/AIContextBar/modules/Constants";
import { createAttachmentsMutation } from "Worksheet/modules/WorksheetMutations";
import {
  pushNewAttachmentToCache,
  deleteAIConversationAttachment,
} from "Worksheet/modules/WorksheetModules";
import { ATTACHMENT_OMIT_ATTRIBUTES } from "store/static";
import TheatreModeV2 from "UIComponents/TheatreModeV2/TheatreModeV2";
import { ENABLE_GOOGLE_DRIVE } from "Constants/permissionConstants";

/** relative */
import classes from "./AddMediaReference.scss";
import AddLinkReference from "./AddLinkReference";
import AddYoutubeReference from "./AddYoutubeReference";

/** constants */
const ALLOWED_FILE_TYPES = _.without(ALLOWED_FILE_TYPES_V2, "application/zip");

// * Converts attachment to a format suitable for the media card
const attachmentWithType = attachment => ({
  ...attachment,
  type:
    _.get(attachment, "mimeType") === "VIDEO"
      ? _.get(attachment, "mimeType")
      : _.get(attachment, "type") || "FILE",
});

/**
 *
 * @param {*} params
 * @returns Upload Media Reference Single File Card
 *
 * For AI-generated files:  
    - The **file type** should be **"Link"** for YouTube and other videos.  
    - On the frontend, we determine the **file type** using `mimeType`.  
    - For all other file types, we use `type`.
 */
const UploadedMediaCard = ({
  t,
  file,
  isDummy = false,
  onClick = _.noop,
  onRemove = _.noop,
}) => {
  return (
    <MediaCard
      dsVersion="2.0"
      cardType="medium"
      onClick={onClick}
      disabled={isDummy}
      url={_.get(file, "url")}
      type={_.get(file, "type")}
      mimeType={_.get(file, "mimeType")}
      testId="worksheet-ai-media-reference-media-card"
      size={isDummy ? null : _.get(file, "metadata.size", 0)}
      name={
        isDummy
          ? t("worksheet:adding_file")
          : _.get(file, "name") || t("common:untitled")
      }
      options={
        !isDummy ? (
          <IconButton
            dsVersion="2.0"
            onClick={() => onRemove(_.get(file, "id"))}
            icon={<DeleteOutlined variant="subtle" size="xx-small" />}
            variant="neutral"
            shouldStopPropagation
            testId={`worksheet-ai-media-reference-media-card:delete`}
          />
        ) : null
      }
    />
  );
};

/**
 *
 * @param {*} props
 * @returns Add Media Reference Component
 */
const AddMediaReference = props => {
  const {
    aiConversationId,
    maxSize,
    parentId,
    parentType,
    portalType,
    isToddleLibraryPortal,
    setToastMsg,
    attachments,
    deleteAIConversationAttachment,
    inProgressAttachments = [],
    setInProgressAttachments = () => {},
  } = props;
  const { t } = useTranslation();
  const hasMediaFile = !_.isEmpty(attachments);
  const disabled = !_.isEmpty(inProgressAttachments);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [activeAttachmentIndex, setActiveAttachmentIndex] = useState(0);
  const isGoogleDriveEnabled = ACLStore.can(ENABLE_GOOGLE_DRIVE);

  const onUpload = async (newFiles, cb) => {
    if (_.isEmpty(newFiles)) return;

    setInProgressAttachments(_.isArray(newFiles) ? newFiles : [newFiles]);

    // * Creating attachment
    await client.mutate({
      mutation: createAttachmentsMutation,
      variables: {
        input: _.map(_.isArray(newFiles) ? newFiles : [newFiles], newFile => ({
          ..._.omit(newFile, ["id", ...ATTACHMENT_OMIT_ATTRIBUTES]),
          parentId: aiConversationId,
          parentType: "AI_CONVERSATION",
        })),
      },
      // Library route defaults auth to COMMUNITY (oid: "-7"); force PLANNER to use teacher's real org token.
      context: portalType === "PLANNER" ? { portalType: "PLANNER" } : undefined,
      update: (
        cache,
        {
          data: {
            platform: { createAttachment },
          },
        }
      ) => {
        pushNewAttachmentToCache({
          aiConversationId,
          attachments: createAttachment,
        });

        setInProgressAttachments([]);

        if (_.isFunction(cb)) cb();
      },
    });
  };

  const onRemove = id => {
    deleteAIConversationAttachment({
      input: { attachmentIds: [id] },
      aiConversationId,
      ...(isToddleLibraryPortal ? { portalType: "PLANNER" } : {}),
    });
  };

  return (
    <div className={classes.container}>
      {hasMediaFile &&
        _.map(attachments, (attachment, index) => {
          return (
            <UploadedMediaCard
              t={t}
              isDummy={false}
              onRemove={onRemove}
              key={_.get(attachment, "url")}
              file={attachmentWithType(attachment)}
              onClick={() => {
                setShowMediaModal(true);
                setActiveAttachmentIndex(index);
              }}
            />
          );
        })}

      {_.map(inProgressAttachments, attachment => {
        return (
          <UploadedMediaCard
            t={t}
            isDummy={true}
            key={_.get(attachment, "url")}
            file={attachmentWithType(attachment)}
          />
        );
      })}

      <div
        className={classes.mediaUploadContainer}
        style={{ pointerEvents: disabled ? "none" : "auto" }}
      >
        {!hasMediaFile && (
          <div className="text-label">{t("worksheet:add_media_reference")}</div>
        )}
        <div className={classes.mediaButtonsContainer}>
          <DropzoneComponent
            multiple={true}
            accept={ALLOWED_FILE_TYPES}
            updateFilesCount={() => {}}
            showConfirmationDialogue={true}
            onContentUploaded={onUpload}
            portalType={portalType}
            renderComponent={
              <Tooltip
                dsVersion="2.0"
                tooltip={t("common:upload_device")}
                showArrow={true}
                placement="bottom"
              >
                <IconButton
                  dsVersion="2.0"
                  variant="neutral"
                  type="outlined"
                  size="small"
                  icon={<UploadOutlined />}
                  testId={"worksheet-ai-media-reference-dropzone"}
                />
              </Tooltip>
            }
          />

          {isGoogleDriveEnabled && (
            <GooglePicker
              maxSize={maxSize}
              parentId={parentId}
              parentType={parentType}
              multiselect={true}
              shouldUpload={true}
              exportAsPdf={true}
              enableShareModal={false}
              pickerType="GoogleDrive"
              accept={ALLOWED_FILE_TYPES}
              onMultipleFilesUploaded={onUpload}
              portalType={portalType}
              renderComponent={
                <Tooltip
                  dsVersion="2.0"
                  tooltip={t("common:google_drive")}
                  showArrow={true}
                  placement="bottom"
                >
                  <IconButton
                    dsVersion="2.0"
                    variant="neutral"
                    type="outlined"
                    size="small"
                    icon={<GoogleDriveColored />}
                    testId="worksheet-ai-media-reference-google-drive"
                  />
                </Tooltip>
              }
            />
          )}

          {isGoogleDriveEnabled && (
            <MicrosoftPicker
              maxSize={maxSize}
              exportAsPdf={true}
              multiselect={true}
              parentId={parentId}
              parentType={parentType}
              pickerType="GoogleDrive"
              enableShareModal={false}
              accept={ALLOWED_FILE_TYPES}
              onMultipleFilesUploaded={onUpload}
              portalType={portalType}
              renderComponent={
                <Tooltip
                  dsVersion="2.0"
                  tooltip={t("common:one_drive_with_space")}
                  showArrow={true}
                  placement="bottom"
                >
                  <IconButton
                    dsVersion="2.0"
                    variant="neutral"
                    type="outlined"
                    size="small"
                    icon={<OneDriveColored />}
                    testId="worksheet-ai-media-reference-one-drive"
                  />
                </Tooltip>
              }
            />
          )}

          <AddYoutubeReference
            t={t}
            onUpload={onUpload}
            setToastMsg={setToastMsg}
          />

          <AddLinkReference
            onUpload={onUpload}
            parentId={parentId}
            parentType={parentType}
          />
        </div>
      </div>

      {showMediaModal && (
        <TheatreModeV2
          activeAttachmentIndex={activeAttachmentIndex}
          toggleTheatreMode={() => setShowMediaModal(!showMediaModal)}
          mediaAttachments={{
            attachments: _.map(attachments, attachmentWithType),
          }}
        />
      )}
    </div>
  );
};

const mapStateToProps = state => {
  const isCurriculumProgramFree = getCurriculumProgramFreeStatus({ state });
  const maxSize = getMaxSize({ isCurriculumProgramFree });

  const parentId = _.get(state, "worksheet.parentEntity.id", null);
  const parentType = _.get(state, "worksheet.parentEntity.type", null);
  const isToddleLibraryPortal =
    _.get(state, "login.activeTab") === "TODDLE_LIBRARY";
  // Library route defaults auth to COMMUNITY (oid: "-7"); force PLANNER to use teacher's real org token.
  const portalType = isToddleLibraryPortal
    ? "PLANNER"
    : _.get(state, "login.userInfo.portalType");

  return { maxSize, parentId, parentType, portalType, isToddleLibraryPortal };
};

const mapActionCreators = {
  setToastMsg,
  deleteAIConversationAttachment,
};

export default compose(connect(mapStateToProps, mapActionCreators))(
  AddMediaReference
);
